<template>
  <SbPortal ref="portalRef" :target="portalTarget" :disabled="!usePortal">
    <component
      :is="parentElementTag"
      :id="anchorId"
      ref="elementRef"
      v-click-outside="{
        handler: wrapClose,
      }"
      class="sb-popover"
      v-bind="$attrs"
      :style="computedStyle"
    >
      <slot />
      <SbPopoverArrow v-if="showPopoverArrow" />
    </component>
  </SbPortal>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  useId,
  useTemplateRef,
  onBeforeUnmount,
  onMounted,
  nextTick,
  watch,
  type HTMLAttributes,
} from 'vue'
import {
  arrow,
  flip,
  hide,
  offset,
  preventOverflow,
} from '@popperjs/core/lib/modifiers'
import { createPopper } from '@popperjs/core/lib/popper-lite'

import SbPortal from '../Portal'
import SbPopoverArrow from './PopoverArrow.vue'

import { ClickOutside as vClickOutside } from '../../directives'
import { canUseDOM } from '../../utils'
import type { Instance, Placement } from '@popperjs/core'

type PopoverProps = {
  placement?: Placement
  strategy?: 'absolute' | 'fixed'
  offset?: [number, number]
  isOpen?: boolean
  parentElementTag?: keyof HTMLElementTagNameMap
  reference?: string | Element
  usePortalTarget?: string
  usePortal?: boolean
  zIndex?: number
  showPopoverArrow?: boolean
} & /* @vue-ignore */ HTMLAttributes

const props = withDefaults(defineProps<PopoverProps>(), {
  placement: 'auto',
  strategy: undefined,
  offset: () => [0, 8],
  isOpen: false,
  parentElementTag: 'div',
  reference: '',
  usePortalTarget: '',
  usePortal: false,
  zIndex: 200,
  showPopoverArrow: false,
})

const emit = defineEmits<{
  (e: 'show'): void
  (e: 'hide'): void
  (e: 'popover-placement', placement: Placement): void
}>()

const anchorId = ref(`sb-popover-${useId()}`)
const popoverInstance = ref<Instance | null>(null)
const elementRef = useTemplateRef<HTMLElement | null>('elementRef')
const portalRef = useTemplateRef<InstanceType<typeof SbPortal> | null>(
  'portalRef',
)

const computedStyle = computed(() => {
  const styles: Partial<CSSStyleDeclaration> = {
    display: props.isOpen ? 'unset' : 'none',
    zIndex: props.zIndex.toString(),
  }

  if (props.strategy) {
    styles.position = props.strategy
  }

  return styles
})
const portalTarget = computed(() => {
  return props.usePortalTarget || `sb-portal-target-${useId()}`
})
const referenceEl = computed(() => {
  if (
    props.reference &&
    typeof props.reference === 'string' &&
    props.reference.length > 0 &&
    canUseDOM
  ) {
    return document.querySelector(props.reference)
  }

  if (props.reference instanceof Element) {
    return props.reference
  }

  return undefined
})
const popoverEl = computed(() => {
  if (props.usePortal && canUseDOM) {
    return document.getElementById(anchorId.value)
  }
  return elementRef.value
})

const wrapClose = (e: Event) => {
  const IsReferenceString = typeof referenceEl.value === 'string'
  const hasContains =
    !IsReferenceString && typeof referenceEl.value?.contains === 'function'
  const targetIsNode = e?.target instanceof Node
  if (
    popoverInstance.value &&
    !IsReferenceString &&
    hasContains &&
    targetIsNode &&
    !referenceEl.value.contains(e?.target)
  ) {
    hidePopover()
  }
}

const showPopover = () => {
  emit('show')

  if (popoverInstance.value) {
    popoverEl.value?.setAttribute('data-show', '')
  }

  nextTick(() => {
    createPopoverInstance()
  })
}

const hidePopover = () => {
  if (popoverInstance.value) {
    popoverEl.value?.removeAttribute('data-show')
    emit('hide')
    destroyPopoverInstance()
  }
}

const createPopoverInstance = () => {
  if (props.usePortal && portalRef.value) {
    portalRef.value?.mountTarget()
  }

  if (popoverInstance.value) {
    popoverEl.value?.setAttribute('data-show', '')
    popoverInstance.value.update()
  } else {
    if (referenceEl.value && popoverEl.value) {
      popoverInstance.value = createPopper(referenceEl.value, popoverEl.value, {
        placement: props.placement,
        modifiers: [
          flip,
          preventOverflow,
          offset,
          {
            name: 'offset',
            options: {
              offset: props.offset,
            },
          },
          arrow,
          hide,
        ],
      })

      popoverInstance.value.update()

      nextTick(() => {
        emit('popover-placement', props.placement)
      })
    }
  }
}

const destroyPopoverInstance = () => {
  if (popoverInstance.value) {
    popoverInstance.value.destroy()
    popoverInstance.value = null
  }
}

watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      showPopover()
    } else {
      hidePopover()
    }
  },
)

watch(
  () => props.placement,
  (newPlacement) => {
    if (popoverInstance.value) {
      popoverInstance.value.state.options.placement = newPlacement
      referenceEl.value?.setAttribute('data-show', '')
    }
  },
)

onMounted(() => {
  if (props.isOpen && !popoverInstance.value) {
    createPopoverInstance()
  }
})

onBeforeUnmount(() => {
  destroyPopoverInstance()
})
</script>
